import './index.scss';
import { jsonArrayFormatValidator } from '../../../../utils/json';
import React, { Fragment, useEffect, useState } from 'react';
import ChatGPTPresetDrawer from '../ChatGPTPresetDrawer';
import { connect } from 'react-redux';
import { toggleControl } from '../../../../store/control/duck';
import { injectIntl } from 'react-intl';
import { Grid, FormControl, FormControlLabel, RadioGroup, Radio, TextareaAutosize, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TextField } from '@mui/material';
import { withRouter } from 'react-router';
import FollowUpButton from '../FollowUpButton/FollowUpButton';
import ActionButton from '../ActionButton/ActionButton';
import { decreaseGPTQuota, handleError, resetGPTQuota, validateGPTQuota } from '../../../../utils/openai';
import LoadingBackdrop from '../../../LoadingBackdrop/LoadingBackdrop';
import ContentBox from '../ContentBox/ContentBox';
import { vertexGenerateWithTextStreaming } from '../../../../store/vertex/api';
import './index.scss';
import { trackDirect } from '../../../../store/analytics/api';
import { tracking } from '../../../../utils/tracking';
import { dialetOptions, toneOptions } from '../Inputs/constant';
import ExampleChips from '../ExampleChips/ExampleChips';
import { multilineTextInputSx } from '../Inputs/inputStyles';
import FormLabelWithIcon from '../Inputs/FormLabelWithIcon';

// Categories, not messages: the box wants a whole message, so a chip fills a
// sentence for the adviser to edit rather than a label to expand.
const REWRITE_EXAMPLES = [
  { labelId: 'gpt-example-rewrite-1', valueId: 'gpt-example-rewrite-1-text' },
  { labelId: 'gpt-example-rewrite-2', valueId: 'gpt-example-rewrite-2-text' },
  { labelId: 'gpt-example-rewrite-3', valueId: 'gpt-example-rewrite-3-text' }
];

const ChatGPTRewriteMessage = props => {
  const { intl, chatGPTRewriteMessageDrawer, toggleControl, extraInformation } = props;
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const [promps, setPromps] = useState({
    client: chatGPTRewriteMessageDrawer?.client,
    dialet: dialetOptions[0].value,
    tone: toneOptions[0].value,
    extra: extraInformation,
    message: chatGPTRewriteMessageDrawer?.message
  });

  const counterEmoji = ['1️⃣', '2️⃣', '3️⃣'];

  useEffect(() => {
    setPromps({
      ...promps,
      client: chatGPTRewriteMessageDrawer.client,
      message: chatGPTRewriteMessageDrawer.message
    });
  }, [chatGPTRewriteMessageDrawer.client, chatGPTRewriteMessageDrawer.message]);

  const [options, setOptions] = useState([]);

  const isOpen = !!chatGPTRewriteMessageDrawer && chatGPTRewriteMessageDrawer.open;

  const onToneChange = event => {
    setPromps({ ...promps, tone: event.target.value });
  };

  const onDialetChange = event => {
    setPromps({ ...promps, dialet: event.target.value });
  };

  const onClose = () => {
    // console.log('chatGPTRewriteMessageDrawer onClose callled');
    toggleControl('chatGPTRewriteMessageDrawer', { open: false });
    setOptions([]);
    // history.push({pathname: history.location.pathname, state: { } });
  };

  const onExited = () => {
    // console.log('chatGPTRewriteMessageDrawer onExit callled');
    toggleControl('chatGPTRewriteMessageDrawer', { open: false });
    setOptions([]);
    // history.push({pathname: history.location.pathname, state: { } });
  };

  const onFeedback = () => {
    toggleControl('chatGPTFeedbackDialog', {
      client: chatGPTRewriteMessageDrawer.client,
      extraContent: options.toString(),
      onSubmit: () => {
        resetGPTQuota();
      }
    });
  };

  const onValue = value => {
    if (chatGPTRewriteMessageDrawer.onValue) chatGPTRewriteMessageDrawer.onValue(value);
    if (chatGPTRewriteMessageDrawer.closeOnValue) onClose();
  };

  const completeAction = result => {
    try {
      setOptions(jsonArrayFormatValidator(result.replace('```json', '').replace('```', '')));
    } catch {
      setOptions(jsonArrayFormatValidator([]));
    }
  };

  const send = async () => {
    try {
      validateGPTQuota();
      setLoading(true);
      trackDirect('useAiFeature');
      trackDirect('rewritePersonalizedMessage');
      tracking('AI: Rewrite Personalized Message');

      let promptData = {
        client_name: promps.client?.factsheetId?.name,
        client_age: promps.client?.factsheetId?.age,
        client_tags: promps.client?.factsheetId?.tags && promps.client.factsheetId.tags.map(tag => tag.text),
        client_personality: promps.client?.factsheetId?.personality,
        client_gender: promps.client?.factsheetId?.gender,
        client_marriage_status: promps.client?.factsheetId?.marriageStatus,
        client_child: promps.client?.factsheetId?.child,
        client_monthly_income: promps.client?.factsheetId?.monthlyIncome,
        message: promps.message,
        dialet: promps.dialet,
        tone: promps.tone
      };
      await vertexGenerateWithTextStreaming(
        'rewrite_message_with_dialet',
        promptData,
        [],
        null,
        completeAction,
        setLoading
      );

      decreaseGPTQuota();
    } catch (error) {
      handleError(error);
      setLoading(false);
    }
  };

  return (
    <ChatGPTPresetDrawer
      open={isOpen}
      onClose={onClose}
      onExited={onExited}
      onFeedback={onFeedback}
      title={intl.formatMessage({ id: 'gpt-message-rewrite-title' })}
      subtitle={intl.formatMessage({ id: 'gpt-subtitle-rewrite' })}
      showQuota
    >
      <LoadingBackdrop open={loading} />
      <Grid container spacing={2} wrap="nowrap" style={{ minHeight: '100%' }} sx={{ flexDirection: 'column' }}>
        <Grid>
          <FormLabelWithIcon
            iconName="FileText"
            labelText={intl.formatMessage({ id: 'gpt-message-rewrite-description' })}
            type="blue"
            required
          />
        </Grid>
        <Grid>
          <ExampleChips
            labelId="gpt-examples-try-messages"
            examples={REWRITE_EXAMPLES}
            value={promps.message}
            onSelect={value => setPromps(current => ({ ...current, message: value }))}
          />
          {/* Controlled. It was `defaultValue`, which React reads once on mount
              — so the box ignored every later change to `promps.message`. That
              already broke reopening the drawer with a different message (the
              effect above sets exactly this field from the control payload);
              it would also have made a chip appear to do nothing. */}
          <TextField
            variant="outlined"
            fullWidth
            value={promps.message ?? ''}
            onChange={event => {
              setPromps({ ...promps, message: event.target.value });
            }}
            placeholder={intl.formatMessage({ id: 'gpt-message-rewrite-input-placeholder' })}
            error={promps.message === '' || promps.message === undefined}
            multiline
            slotProps={{ input: { rows: 3, sx: multilineTextInputSx } }}
          />
        </Grid>

        <Grid>
          <FormLabelWithIcon
            iconName="Smile"
            labelText={intl.formatMessage({ id: 'gpt-message-rewrite-targeted-message-tone-title' })}
            type="green"
          />
          <FormControl style={{ padding: '0px 8px' }}>
            <RadioGroup
              row
              aria-label="targeted-message-tone"
              name="targeted-message-tone"
              value={promps.tone}
              onChange={onToneChange}
            >
              {toneOptions.map((option, index) => (
                <FormControlLabel
                  style={{ padding: '0px 2px 0px 0px' }}
                  key={option.value + index}
                  value={option.value}
                  control={<Radio color="primary" style={{ padding: '0px 2px 2px 0px' }} />}
                  label={intl.formatMessage({ id: option.label })}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid>
          <FormLabelWithIcon
            iconName="MessageSquare"
            labelText={intl.formatMessage({ id: 'gpt-message-rewrite-targeted-message-dialet-title' })}
            type="purple"
          />
          <FormControl style={{ padding: '0px 8px' }}>
            <RadioGroup
              row
              aria-label="gpt-message-rewrite-dialet"
              name="targeted-message-dialet"
              value={promps.dialet}
              onChange={onDialetChange}
            >
              {dialetOptions.map((option, index) => (
                <FormControlLabel
                  style={{ padding: '0px 2px 0px 0px' }}
                  key={option.value + index}
                  value={option.value}
                  control={<Radio color="primary" style={{ padding: '0px 2px 2px 0px' }} />}
                  label={intl.formatMessage({ id: option.label })}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid>
          <Grid
            container
            sx={{
              justifyContent: 'space-between'
            }}
          >
            <Grid style={{ width: '100%' }}>
              <ActionButton onClick={send}>{intl.formatMessage({ id: 'gpt-message-rewrite-generate' })}</ActionButton>
            </Grid>
          </Grid>
        </Grid>

        {options.length >= 1 && (
          <Grid>
            {<span style={{ fontSize: 16 }}>{intl.formatMessage({ id: 'choice' })}</span>}
            {options.map((option, index) => (
              <FollowUpButton
                key={index}
                onClick={() => onValue(option)}
                color="primary"
                style={{ color: theme.palette.primary.main, marginLeft: 10 }}
              >
                {intl.formatMessage(
                  { id: 'option-number' },

                  { number: index + 1 }
                )}
              </FollowUpButton>
            ))}
          </Grid>
        )}
        <Grid style={{ width: '100%' }}>
          <ContentBox
            text={
              process.env.NODE_ENV === 'development'
                ? (promps.details ? promps.details + '\n' : '') +
                  (options.length === 0
                    ? ''
                    : options.map((e, index) => `💬 選項 ${counterEmoji[index]} \n${e}\n\n`).join(''))
                : options.length === 0
                ? ''
                : options.map((e, index) => `選項${index + 1}:\n${e}\n\n`).join('')
            }
            // text={promps.detail}
            disableCopy={false}
          />
          <br />
        </Grid>
        <Grid>
          <Grid
            container
            sx={{
              justifyContent: 'space-between'
            }}
          ></Grid>
        </Grid>
      </Grid>
    </ChatGPTPresetDrawer>
  );
};

const container = connect(
  state => ({
    chatGPTRewriteMessageDrawer: state.control.chatGPTRewriteMessageDrawer
  }),
  {
    toggleControl,
    resetGPTQuota
  }
)(ChatGPTRewriteMessage);

export default injectIntl(container);
